import React from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { FaWindowClose, FaKey } from 'react-icons/fa';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';

// D4 — Modal "Dados Complementar para Gerar Chave".
// Réplica fiel do setup aplicado em uDebitosIPTU.pas:712-794
// (btnLancarChaveClick → reuso off-label do fExcelGestaoIPTU):
//   • cbDespesa só 3 opções: Selecione / 90015 / 90034 (sem "Outras Despesas")
//   • mApuracao + cbModalidade desabilitados (Modalidade é derivada do código
//     de barras dentro de btnLancarClick, não escolhida pelo usuário)
//   • cbTipoVencimento visível com default = "Manual" (ItemIndex=0)
//   • sem seletor de GE; o Delphi usa o GE do próprio débito retornado
//   • Caption = "Dados Complementar para Gerar Chave"
const OPCOES_DESPESA = [
  { value: '',      label: 'Selecione' },
  { value: '90015', label: '90015 - IPTU lotes em estoque' },
  { value: '90034', label: '90034 - IPTU Reembolso Clientes' },
];

const OPCOES_TIPO_DOCUMENTO = [
  { value: '',   label: 'Selecione' },
  { value: '01', label: '01 - Boleto' },
  { value: '08', label: '08 - Tributos' },
];

const hoje = moment().startOf('day');

const schema = Yup.object({
  despesa: Yup.string().required('Despesa deve ser selecionada.'),
  tipoVencimento: Yup.string().oneOf(['manual', 'boleto']).required(),
  vencimento: Yup.date().nullable().when('tipoVencimento', {
    is: 'manual',
    then: Yup.date()
      .required('Vencimento deve ser informado.')
      .min(hoje.toDate(), 'Vencimento não pode ser menor que a data atual.'),
  }),
  pagarEm: Yup.date().nullable().when('tipoVencimento', {
    is: 'manual',
    then: Yup.date()
      .required('Pagar Em deve ser informado.')
      .min(hoje.toDate(), 'Pagar Em não pode ser menor que a data atual.'),
  }),
  tipoDocumento: Yup.string().required('Tipo de Documento deve ser selecionado.'),
});

const ModalDadosChave = ({ show, onHide, onConfirmar, carregando }) => {
  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>Dados Complementar para Gerar Chave</Modal.Title>
      </Modal.Header>
      <Formik
        enableReinitialize
        initialValues={{
          despesa: '',
          tipoVencimento: 'manual',
          vencimento: null,
          pagarEm: null,
          tipoDocumento: '',
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          if (!window.confirm('Todas as informações estão corretas?')) return;
          const vencimentoDoBoleto = values.tipoVencimento === 'boleto';
          onConfirmar({
            despesa: values.despesa,
            vencimento: vencimentoDoBoleto || !values.vencimento
              ? null
              : moment(values.vencimento).format('YYYY-MM-DD'),
            pagarEm: vencimentoDoBoleto || !values.pagarEm
              ? null
              : moment(values.pagarEm).format('YYYY-MM-DD'),
            tipoDocumento: values.tipoDocumento,
            vencimentoDoBoleto,
          });
        }}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit, handleChange, handleBlur }) => {
          const desabVencManual = values.tipoVencimento !== 'manual';
          return (
            <Form onSubmit={handleSubmit} noValidate>
              <Modal.Body>
                <Row>
                  <Col xs={12} className="mb-2">
                    <Form.Label>Despesa <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="select"
                      name="despesa"
                      value={values.despesa}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.despesa && !!errors.despesa}
                    >
                      {OPCOES_DESPESA.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{errors.despesa}</Form.Control.Feedback>
                  </Col>

                  {/* cbTipoVencimento — uDebitosIPTU.pas:729-734 */}
                  <Col xs={12} className="mb-2">
                    <Form.Label className="d-block">Tipo Vencimento</Form.Label>
                    <Form.Check
                      type="radio"
                      inline
                      id="tipoVenc-manual"
                      name="tipoVencimento"
                      label="Manual"
                      value="manual"
                      checked={values.tipoVencimento === 'manual'}
                      onChange={() => setFieldValue('tipoVencimento', 'manual')}
                    />
                    <Form.Check
                      type="radio"
                      inline
                      id="tipoVenc-boleto"
                      name="tipoVencimento"
                      label="Vencimento do boleto"
                      value="boleto"
                      checked={values.tipoVencimento === 'boleto'}
                      onChange={() => {
                        setFieldValue('tipoVencimento', 'boleto');
                        // Limpa as datas manuais — vencimento/pagar_em virão do
                        // próprio débito retornado pela JPlan no BE.
                        setFieldValue('vencimento', null);
                        setFieldValue('pagarEm', null);
                      }}
                    />
                  </Col>

                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label>Vencimento {!desabVencManual && <span className="text-danger">*</span>}</Form.Label>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <DatePicker
                        value={values.vencimento}
                        onChange={(val) => {
                          setFieldValue('vencimento', val);
                          if (!values.pagarEm) setFieldValue('pagarEm', val);
                        }}
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/AAAA"
                        clearable
                        disabled={desabVencManual}
                        inputVariant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{ style: { fontSize: 13, height: 31 } }}
                      />
                    </MuiPickersUtilsProvider>
                    {touched.vencimento && errors.vencimento && (
                      <div className="text-danger" style={{ fontSize: 12 }}>{errors.vencimento}</div>
                    )}
                  </Col>

                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label>Pagar Em {!desabVencManual && <span className="text-danger">*</span>}</Form.Label>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <DatePicker
                        value={values.pagarEm}
                        onChange={(val) => setFieldValue('pagarEm', val)}
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/AAAA"
                        clearable
                        disabled={desabVencManual}
                        inputVariant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{ style: { fontSize: 13, height: 31 } }}
                      />
                    </MuiPickersUtilsProvider>
                    {touched.pagarEm && errors.pagarEm && (
                      <div className="text-danger" style={{ fontSize: 12 }}>{errors.pagarEm}</div>
                    )}
                  </Col>

                  {/* Apuração e Modalidade omitidos: o Delphi os deixa
                      desabilitados (uDebitosIPTU.pas:718-723) e ambos são
                      irrelevantes pro fluxo de Lançar Chave — Modalidade é
                      derivada do código de barras no BE (btnLancarClick:
                      1014-1023). */}
                  <Col xs={12} md={6} className="mb-2">
                    <Form.Label>Tipo de Documento <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="select"
                      name="tipoDocumento"
                      value={values.tipoDocumento}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.tipoDocumento && !!errors.tipoDocumento}
                    >
                      {OPCOES_TIPO_DOCUMENTO.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{errors.tipoDocumento}</Form.Control.Feedback>
                  </Col>

                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="warning"
                  type="submit"
                  disabled={carregando}
                  className="d-inline-flex align-items-center"
                >
                  {carregando
                    ? <Spinner animation="border" size="sm" className="mr-2" />
                    : <FaKey className="mr-2" />}
                  Gerar Chave
                </Button>
                <Button
                  variant="light"
                  className="d-inline-flex align-items-center"
                  onClick={onHide}
                  disabled={carregando}
                >
                  <FaWindowClose />
                  <span className="ml-2">Fechar</span>
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ModalDadosChave;
